/**
 * ProductQuickView — mini-PDP in a dialog modal. PUBLIC ENTRY.
 *
 * COM-174: the actual quick-view body lives in product-quick-view-impl.tsx
 * behind React.lazy. The impl is the tiles'/PLPs' only path to the PDP
 * component graph (ProductImages -> swiper ~96KB emitted, ProductBuyForm,
 * BundleItems); importing it statically from here put that graph into the
 * always-loaded layout chunk via the Builder block registry -> product tile
 * chain. The quick view renders only after a shopper clicks a tile CTA, so
 * its chunk now loads on that first click. Keep this module's static imports
 * to the lightweight dialog shell.
 *
 * Callers are unchanged: import { ProductQuickView, ProductQuickViewDialog }
 * from this module exactly as before.
 */
import type { ReactNode } from "react";
import { Suspense, lazy } from "react";

import { OverlayBoundary } from "~/components/overlay-boundary";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import { useCloseOnNavigation } from "~/hec/hooks/useCloseOnNavigation";
import type { ProductHitWithSwatches } from "~/lib/app.types";

// ---------------------------------------------------------------------------
// Dialog wrapper
// ---------------------------------------------------------------------------

export function ProductQuickViewDialog({
  open,
  children,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  // Any link inside the quick view (brand row, bundle items, View Full
  // Details, Builder CTAs) must not leave the modal mounted over the
  // destination. Close on navigation here once, instead of per link (COM-104).
  useCloseOnNavigation(onClose, open);
  return (
    <Dialog
      open={open}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[90vh] w-[90%] max-w-[770px] flex-col overflow-y-auto rounded-lg bg-white px-6 py-8 sm:px-10 md:min-h-[640px]"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Quick View</DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Quick View content (lazy)
// ---------------------------------------------------------------------------

// Mirrors the impl component's props; the impl type-checks against this via
// the `satisfies` on the lazy factory below (a drifted impl signature fails
// to assign to React.FC<ProductQuickViewProps>).
export interface ProductQuickViewProps {
  product: ProductHitWithSwatches;
  currentSwatch?: unknown;
  fetcherData?: unknown;
  onClose: () => void;
  /** Pre-fill for the buy-form quantity stepper — the quantity the shopper
   *  already set on the originating tile (SFCC-11825). */
  initialQuantity?: number;
  /**
   * Tile-snapshot opt-in: `"grid"` marks a quick view opened from a PLP /
   * search / hybrid grid tile, letting `/resources/product/:id` serve the D1
   * tile snapshot (the `?from=grid` fence — see `useQuickViewProduct`).
   * Out-of-scope surfaces (recs carousels, favorites) pass nothing and keep
   * the live path and its shared edge-cache entries byte-identical.
   */
  snapshotSource?: "grid";
  /** Called once when an add to cart started in this Quick View succeeds. */
  onAdded?: () => void;
}

const LazyProductQuickView = lazy(() =>
  import("./product-quick-view-impl").then(m => ({
    default: m.ProductQuickView satisfies React.FC<ProductQuickViewProps>,
  })),
);

/**
 * In-dialog fallback when the lazy quick-view chunk itself fails (or the impl
 * throws while rendering). Rendered by the OverlayBoundary below AFTER its
 * module-load reload guard has been consulted — so this is what a shopper sees
 * when the chunk is genuinely unavailable on the current build, never a bare
 * spinner. Close hands control back to the tile; Reload is the shopper-driven
 * retry (a stale chunk hash is fixed by fresh HTML).
 */
function QuickViewChunkError({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="alert"
      data-testid="quick-view-chunk-error"
      className="flex min-h-[300px] flex-col items-center justify-center gap-4 text-center"
    >
      <p className="font-grotesque text-lg font-medium text-primary">
        We couldn&apos;t open Quick View.
      </p>
      <p className="text-sm text-foreground">
        Please reload the page and try again.
      </p>
      <div className="flex gap-3">
        <Button
          variant="default"
          onClick={() => window.location.reload()}
          data-testid="quick-view-reload"
        >
          Reload page
        </Button>
        <Button
          variant="secondary"
          onClick={onClose}
          data-testid="quick-view-close"
        >
          Close
        </Button>
      </div>
    </div>
  );
}

export const ProductQuickView = (props: ProductQuickViewProps) => (
  // OverlayBoundary (closes the C6 gap — the Quick View was the one lazy modal
  // the PR #1042 B3 sweep never covered): a rejected chunk import or a render
  // throw inside the quick view is contained to the dialog instead of
  // unmounting the PLP into RootErrorBoundary. `reloadOnModuleLoadError`
  // routes a stale-chunk (version-skew) failure through the SFCC-11887
  // build-keyed single reload; anything else renders the in-dialog error.
  <OverlayBoundary
    overlay="product-quick-view"
    reloadOnModuleLoadError
    fallback={<QuickViewChunkError onClose={props.onClose} />}
  >
    {/* The fallback mirrors the impl's own product-fetch loading frame (same
        min-h + spinner) so the dialog's first open frame is identical to the
        pre-split behaviour. `fallback={null}` made the freshly-opened dialog
        render near-empty and then jump ~300px taller when the lazy chunk
        landed — the dialog's `md:min-h-[640px]` only masks that on desktop. */}
    <Suspense
      fallback={
        <div
          className="flex min-h-[300px] items-center justify-center"
          data-testid="quick-view-loading"
        >
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-accent" />
        </div>
      }
    >
      <LazyProductQuickView {...props} />
    </Suspense>
  </OverlayBoundary>
);
